import { Context } from '@nuxt/types'
import { IncomingMessage } from 'connect'
import * as cookie from 'cookie'
import objectHash from 'node-object-hash'
import md5 from 'crypto-js/md5'
import { FeaturesConfig } from '@mg/web-components/types'
import { AppConfig } from '~/config/appConfig.type'
import { WebApi } from '~/utils/webApi'
import { calcAbTests, CalcAbTestsResult } from '~/module/shopConfig/calcAbTests'
import { getFeaturesConfig } from '~/module/shopConfig/getFeaturesConfig'
import { Shops } from '~/config/shops'
import { WebConfig } from '~/config/shopConfig'
import { ShopConfigQueryResult } from '~/server/services/shopConfigLoader/shopConfigLoader.types'

export interface ShopConfigContext extends Context {
  shopConfig: ShopConfig
  req: ShopConfigContextReq
}

export interface ShopConfigContextReq extends IncomingMessage {
  env: AppConfig
  isBot: Boolean
  shopConfigData: ShopConfigQueryResult
}

export interface Tests {
  [key: string]: string
}

export interface ShopConfigInject {
  inject: (key: string, value: ShopConfig | WebApi) => void
}

export interface Cookies {
  abTests?: {
    tests: Tests
    hash: string
  }
  edeRollout: {
    isCookieSet: boolean
    randomNumber: number // NOTE: we must persist same random number between client and server
    value: boolean
  }
  search: {
    searchMirror: boolean
    dbUi: string | null
  }
}

export interface ShopConfig {
  shopId: Shops
  shopUrl: string
  csrftoken: string
  cookies: Record<string, string>
  featuresConfig: FeaturesConfig
  isBot: boolean
  cartId: string
  abTests: {
    cookies: Cookies
    abTestsData: CalcAbTestsResult
  }
  webConfig: WebConfig
  user: {
    id?: string
    gtmCustomerId?: string
    email?: string
    isAdult?: boolean
    isInternal?: boolean
    isEmployee?: boolean
    pricingGroup?: string
  }
}

const parseCookies = (cookiesString: string | undefined): Cookies => {
  const cookies = cookiesString ? cookie.parse(cookiesString) : {}
  const cookiesKeys = Object.keys(cookies)
  const abtests: Tests = {}
  const edeRollout: Cookies['edeRollout'] = {
    isCookieSet: false,
    randomNumber: Math.random(),
    value: false,
  }

  const search: Cookies['search'] = {
    searchMirror: false,
    dbUi: null,
  }

  cookiesKeys.forEach((key) => {
    if (key.includes('_mgExp-')) {
      abtests[key] = cookies[key]
    }
    if (key.includes('ede_rollout_cookie')) {
      edeRollout.isCookieSet = true
      edeRollout.value = cookies[key] === '1'
    }
    if (key.includes('search_mirror')) {
      search.searchMirror = true
    }

    if (key === 'db_ui' && cookies[key]) {
      search.dbUi = cookies[key]
    }
  })

  return {
    abTests: {
      hash: md5(objectHash().sortObject(abtests)).toString(),
      tests: abtests,
    },
    edeRollout,
    search,
  }
}

const shopConfigMapper = (ctx: ShopConfigContext): ShopConfig => {
  const req: ShopConfigContextReq = ctx.req
  const shopId =
    <Shops>req.headers['x-shop-id'] ||
    req.env.graphqlUrbanHeaderShopId ||
    Shops.CZ10MA

  const user = req.headers['x-user']
    ? JSON.parse(<string>req.headers['x-user'])
    : ctx.env.GRAPHGQL_URBAN_HEADER_USER?.length > 0
    ? JSON.parse(ctx.env.GRAPHGQL_URBAN_HEADER_USER)
    : {
        email: null,
        gtmCustomerId: null,
        id: null,
        isAdult: null,
        isInternal: null,
        isEmployee: null,
        pricingGroup: null,
      }

  // @ts-ignore
  const shopUrl = req.env[`shopUrl.${shopId}`]
  const csrftoken = (req.headers['x-form-token'] || req.env.csrfToken) as string
  const cartId = (req.headers['x-cart-id'] ?? null) as string

  const getConfig: ShopConfigQueryResult = req.shopConfigData

  const abTestsData = {
    campaignAbTests: getConfig.campaignAbTests,
    menuAbTests: getConfig.menuAbTests,
  }

  const webConfig: WebConfig = getConfig.shopConfig as WebConfig
  const abTestCookies = parseCookies(req.headers.cookie)

  const cookies = req.headers.cookie ? cookie.parse(req.headers.cookie) : {}

  return <ShopConfig>{
    abTests: {
      abTestsData: calcAbTests({ abTestsData, cookies: abTestCookies }),
      cookies: abTestCookies,
    },
    cookies,
    webConfig,
    cartId,
    csrftoken,
    featuresConfig: getFeaturesConfig(webConfig, cookies),
    isBot: req.isBot,
    shopId,
    shopUrl,
    user,
  }
}

const strategies = {
  client(ctx: ShopConfigContext, inject: ShopConfigInject['inject']) {
    const shopConfig =
      ctx.nuxtState !== undefined ? ctx.nuxtState.shopConfig : ctx.shopConfig
    inject('shopConfig', shopConfig)

    const csrfToken = shopConfig.csrftoken
    inject('webApi', new WebApi(csrfToken))
  },

  async server(ctx: ShopConfigContext, inject: ShopConfigInject['inject']) {
    const shopConfig = await shopConfigMapper(ctx)

    inject('shopConfig', {
      ...shopConfig,
    })

    const csrfToken = shopConfig.csrftoken
    inject('webApi', new WebApi(csrfToken))

    ctx.beforeNuxtRender(({ nuxtState }) => {
      nuxtState.shopConfig = {
        ...shopConfig,
      }
    })
  },
}

export default (ctx: ShopConfigContext, inject: ShopConfigInject['inject']) => {
  const key = process.client ? 'client' : 'server'
  const strategy = strategies[key]
  return strategy(ctx, inject)
}
